// GeomDelta
//
// One way of writing "what this revision did to the geometry", used
// everywhere a revision is listed: Export history (all four layouts),
// the unified model timeline, and the model tree rail inside the viewer.
//
// It is deliberately GIT-SHAPED - `+3 ~7 −5`, added / modified / deleted,
// in that order and in those colours - and it ALWAYS renders, including
// the all-zero case. A row that hides its counts when nothing changed
// reads as "no data" rather than "no change", and the eye then has to
// re-learn the format every time it reappears; a steady `+0 ~0 −0` is
// read at a glance and is honest about a push that touched nothing.
//
// `removed` carries the qualifier the number needs: deletions are
// inferred from absence, and a SELECTION push (only the elements its
// author picked) cannot testify to them at all.
import React from "react";
import { Tooltip } from "antd";

export const DELTA_COLORS = {
  added: "#3F9142",
  modified: "#B07A1E",
  removed: "#C4443F",
  zero: "#9AA7B0",
};

/**
 * @param {number} added
 * @param {number} modified
 * @param {number} removed
 * @param {boolean} inferred   selection push - removals cannot be known
 * @param {"sm"|"md"} size
 * @param {boolean} boxed      draw the pill background (list rows) or bare (inline)
 */
export default function GeomDelta({
  added = 0, modified = 0, removed = 0,
  inferred = false, size = "md", boxed = true, style,
}) {
  const fs = size === "sm" ? 10.5 : 11.5;
  const cell = (prefix, n, color, title) => {
    const body = (
      <span style={{
        color: n ? color : DELTA_COLORS.zero,
        fontWeight: n ? 700 : 500,
        fontVariantNumeric: "tabular-nums",
      }}>
        {prefix}{n || 0}
      </span>
    );
    return title ? <Tooltip title={title}>{body}</Tooltip> : body;
  };

  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        fontSize: fs, lineHeight: 1,
        fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
        ...(boxed
          ? {
              padding: size === "sm" ? "2px 6px" : "3px 8px",
              borderRadius: 4, background: "#F9FAFB", border: "1px solid #E2E2E2",
            }
          : {}),
        ...style,
      }}
    >
      {cell("+", added, DELTA_COLORS.added, "Elements this push added")}
      {cell("~", modified, DELTA_COLORS.modified, "Elements this push changed")}
      {cell("−", removed, DELTA_COLORS.removed, inferred
        ? "A selection push carries only the elements its author chose, so it cannot testify to deletions."
        : "Deletions are inferred from absence - an element that failed to export looks the same.")}
    </span>
  );
}

// The same numbers pulled out of whichever shape the caller has: a
// commit row from /commits/timeline, a CommitDiff summary, or the
// estimation lane's diff document. Returns null when the row carries no
// geometry lane at all (a pure estimate export), so the caller can print
// an em dash rather than a misleading row of zeros.
export function deltaOf(row) {
  if (!row) return null;
  const m = row.model;
  if (m && (m.added != null || m.modified != null || m.removed != null)) {
    return { added: m.added || 0, modified: m.modified || 0, removed: m.removed || 0 };
  }
  const s = row.diff && row.diff.summary;
  if (s) {
    return {
      added: s.componentsAdded || 0,
      modified: s.componentsModified || 0,
      removed: s.componentsRemoved || 0,
    };
  }
  return null;
}
